import { WFS_DEFECT_SOURCE } from '@/features/defect-drawer/utils/defectFilter';
import {
  findModuleInSiteDesignHierarchy,
  logModuleListDebug,
  summarizeDefectModuleLinkage
} from '@/features/defect-drawer/utils/moduleListSyncDebug';
import { normalizeDefectStatus } from '@/services/defect/defectStatus.utils';

export const MODULE_LIST_CREATE_CHANGE_TYPE = 'create-module-list';

export function shouldSkipFullDefectRefresh(changeType) {
  return changeType === 'delete' || changeType === MODULE_LIST_CREATE_CHANGE_TYPE;
}

export function allocateOptimisticDefectSeq(existingRows = []) {
  const used = existingRows
    .map(row => Number(row?.seq))
    .filter(seq => Number.isFinite(seq) && seq < 0);
  if (!used.length) {
    return -1;
  }
  return Math.min(...used) - 1;
}

export function resolveModuleListCreateContext(hierarchy, moduleSeq) {
  if (moduleSeq == null || moduleSeq === '') {
    return null;
  }
  const fromDesign = findModuleInSiteDesignHierarchy(hierarchy, moduleSeq);
  if (fromDesign) {
    return {
      solarModuleSeq: fromDesign.moduleSeq,
      moduleId: fromDesign.moduleDisplayId ?? null,
      solarStringSeq: fromDesign.stringSeq ?? null,
      stringId: fromDesign.stringDisplayId ?? null,
      solarArraySeq: fromDesign.arraySeq ?? null,
      arrayId: fromDesign.arrayDisplayId ?? null
    };
  }
  return { solarModuleSeq: moduleSeq };
}

export function buildOptimisticModuleListCreateRow({
  moduleContext,
  thermalImgSeq,
  defectType,
  defectCode,
  defectStatus,
  detectWay,
  tempSeq
}) {
  return {
    seq: tempSeq,
    solarModuleSeq: moduleContext.solarModuleSeq,
    moduleId: moduleContext.moduleId ?? null,
    stringId: moduleContext.stringId ?? null,
    arrayId: moduleContext.arrayId ?? null,
    solarStringSeq: moduleContext.solarStringSeq ?? null,
    solarArraySeq: moduleContext.solarArraySeq ?? null,
    thermalImgSeq: thermalImgSeq ?? null,
    defectType: defectType ?? null,
    imgName: null,
    defectStatus: normalizeDefectStatus(defectStatus) || 'FOUND',
    mlDefectConfirmed: 'YES',
    detectWay: detectWay ?? 'M',
    wfsSource: WFS_DEFECT_SOURCE.CONFIRMED,
    mapPositionVerified: false,
    defectDesc: null,
    defectCode: defectCode ?? null,
    lng: null,
    lat: null,
    optimisticCreate: true
  };
}

/**
 * 모듈리스트 covering 신규 등록 — 해당 모듈 행이 즉시 panel_single 이 되도록 temp 행을 넣는다.
 * @returns {{ tempSeq: number, row: object } | null}
 */
export function applyOptimisticModuleListCreate(store, options = {}) {
  const moduleSeq = options.moduleSeq;
  if (moduleSeq == null || moduleSeq === '') {
    return null;
  }

  const hierarchy = store.state.dashboard?.siteDesignModuleHierarchy || [];
  const moduleContext = resolveModuleListCreateContext(hierarchy, moduleSeq);
  const rows = store.state.dashboard?.wfsDefectRows || [];
  const tempSeq = allocateOptimisticDefectSeq(rows);
  const row = buildOptimisticModuleListCreateRow({
    moduleContext,
    thermalImgSeq: options.thermalImgSeq,
    defectType: options.defectType,
    defectCode: options.defectCode,
    defectStatus: options.defectStatus,
    detectWay: options.detectWay,
    tempSeq
  });

  logModuleListDebug('create.optimistic', {
    tempSeq,
    solarModuleSeq: row.solarModuleSeq,
    beforeLinkage: summarizeDefectModuleLinkage(rows)
  });
  store.commit('dashboard/insertWfsDefectRow', { row });
  return { tempSeq, row };
}

/** POST 응답 id로 같은 index 행의 seq를 확정한다. */
export function commitOptimisticModuleListCreate(store, { tempSeq, createdId, patch = {} } = {}) {
  if (tempSeq == null || createdId == null) {
    return;
  }
  store.commit('dashboard/replaceWfsDefectRow', {
    fromSeq: tempSeq,
    row: {
      seq: createdId,
      optimisticCreate: false,
      ...patch
    }
  });
}

export function rollbackOptimisticModuleListCreate(store, tempSeq) {
  if (tempSeq == null) {
    return;
  }
  store.commit('dashboard/removeWfsDefectRow', tempSeq);
}
